import { ErrorLogEntry, ErrorLoggerConfig, ErrorSeverity } from './types';

class ErrorLogger {
  private static instance: ErrorLogger;
  private config: ErrorLoggerConfig;
  private errorQueue: ErrorLogEntry[] = [];
  private sessionId: string;
  private flushTimer: NodeJS.Timeout | null = null;

  private constructor(config: Partial<ErrorLoggerConfig> = {}) {
    this.config = {
      enabled: true,
      batchSize: 10,
      flushInterval: 30000, // 30 seconds
      maxErrors: 100,
      includeUserAgent: true,
      includeUrl: true,
      includeUserId: true,
      ...config
    };

    this.sessionId = this.generateSessionId();
    this.startFlushTimer();
  }

  public static getInstance(config?: Partial<ErrorLoggerConfig>): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger(config);
    }
    return ErrorLogger.instance;
  }

  public logError(
    error: Error,
    errorInfo?: React.ErrorInfo,
    severity: ErrorSeverity = 'medium',
    context?: string,
    retryCount: number = 0,
    errorBoundary?: string
  ): string {
    if (!this.config.enabled) {
      return '';
    }

    const errorId = this.generateErrorId();

    const logEntry: ErrorLogEntry = {
      id: errorId,
      timestamp: new Date().toISOString(),
      error: {
        message: error.message,
        name: error.name,
        stack: error.stack
      },
      componentStack: errorInfo?.componentStack,
      errorBoundary,
      severity,
      context,
      userAgent: this.config.includeUserAgent ? navigator.userAgent : '',
      url: this.config.includeUrl ? window.location.href : '',
      userId: this.getUserId(),
      sessionId: this.sessionId,
      retryCount,
      resolved: false
    };

    this.addToQueue(logEntry);
    this.logToConsole(logEntry);

    return errorId;
  }

  public logNetworkError(
    error: Error,
    url: string,
    method: string,
    status?: number
  ): string {
    const context = `Network Error: ${method} ${url}${status ? ` (${status})` : ''}`;
    return this.logError(error, undefined, 'high', context);
  }

  public logSupabaseError(error: Error, operation: string): string {
    const context = `Supabase Error: ${operation}`;
    return this.logError(error, undefined, 'high', context);
  }

  public logAvatarError(error: Error, operation: string): string {
    const context = `Avatar Error: ${operation}`;
    return this.logError(error, undefined, 'medium', context);
  }

  public logReactError(error: Error, errorInfo: React.ErrorInfo): string {
    return this.logError(error, errorInfo, 'critical', undefined, 0, 'ReactErrorBoundary');
  }

  private addToQueue(logEntry: ErrorLogEntry): void {
    this.errorQueue.push(logEntry);

    // Remove old errors if exceeding max
    if (this.errorQueue.length > this.config.maxErrors!) {
      this.errorQueue = this.errorQueue.slice(-this.config.maxErrors!);
    }

    // Flush immediately if batch size reached
    if (this.errorQueue.length >= this.config.batchSize!) {
      this.flush();
    }
  }

  private async flush(): Promise<void> {
    if (this.errorQueue.length === 0) {
      return;
    }

    const errorsToSend = [...this.errorQueue];
    this.errorQueue = [];

    try {
      if (this.config.endpoint && this.config.apiKey) {
        await this.sendToService(errorsToSend);
      }
    } catch (flushError) {
      console.error('Failed to flush error logs:', flushError);
      // Re-add errors to queue on failure
      this.errorQueue.unshift(...errorsToSend.slice(0, this.config.maxErrors! - this.errorQueue.length));
    }
  }

  private async sendToService(errors: ErrorLogEntry[]): Promise<void> {
    if (!this.config.endpoint || !this.config.apiKey) {
      return;
    }

    const response = await fetch(this.config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.config.apiKey}`,
        'X-Session-ID': this.sessionId
      },
      body: JSON.stringify({
        errors,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to send errors: ${response.status} ${response.statusText}`);
    }
  }

  private logToConsole(logEntry: ErrorLogEntry): void {
    const logLevel = this.getLogLevel(logEntry.severity);
    const message = `[${logEntry.severity.toUpperCase()}] ${logEntry.context || 'Unknown'}: ${logEntry.error.message}`;

    console[logLevel](`%c${message}`, this.getLogStyle(logEntry.severity), logEntry);

    if (logEntry.componentStack) {
      console.group('Component Stack');
      console.log(logEntry.componentStack);
      console.groupEnd();
    }

    if (logEntry.error.stack) {
      console.group('Error Stack');
      console.log(logEntry.error.stack);
      console.groupEnd();
    }
  }

  private getLogLevel(severity: ErrorSeverity): 'error' | 'warn' | 'info' {
    switch (severity) {
      case 'critical':
      case 'high':
        return 'error';
      case 'medium':
        return 'warn';
      case 'low':
        return 'info';
      default:
        return 'error';
    }
  }

  private getLogStyle(severity: ErrorSeverity): string {
    switch (severity) {
      case 'critical':
        return 'color: #dc2626; font-weight: bold; background: #fef2f2; padding: 2px 4px; border-radius: 2px;';
      case 'high':
        return 'color: #ea580c; font-weight: bold; background: #fff7ed; padding: 2px 4px; border-radius: 2px;';
      case 'medium':
        return 'color: #d97706; font-weight: bold; background: #fef3c7; padding: 2px 4px; border-radius: 2px;';
      case 'low':
        return 'color: #65a30d; font-weight: normal; background: #f0fdf4; padding: 2px 4px; border-radius: 2px;';
      default:
        return 'color: #374151;';
    }
  }

  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getUserId(): string | undefined {
    // Try to get user ID from auth context or localStorage
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id;
      }
    } catch (error) {
      // Ignore errors
    }
    return undefined;
  }

  private startFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.config.flushInterval);
  }

  public clearQueue(): void {
    this.errorQueue = [];
  }

  public getQueueLength(): number {
    return this.errorQueue.length;
  }

  public getErrorsBySeverity(severity: ErrorSeverity): ErrorLogEntry[] {
    return this.errorQueue.filter(error => error.severity === severity);
  }

  public getErrorStats(): {
    total: number;
    bySeverity: Record<ErrorSeverity, number>;
    byContext: Record<string, number>;
  } {
    const bySeverity: Record<ErrorSeverity, number> = {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0
    };

    const byContext: Record<string, number> = {};

    this.errorQueue.forEach(error => {
      bySeverity[error.severity]++;

      if (error.context) {
        byContext[error.context] = (byContext[error.context] || 0) + 1;
      }
    });

    return {
      total: this.errorQueue.length,
      bySeverity,
      byContext
    };
  }

  public destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // Flush remaining errors before destroy
    this.flush();
  }
}

export { ErrorLogger };